import { useQuery } from '@vue/apollo-composable';
import { computed, ref, watch } from 'vue';

import { CountryCode } from '@/__generated__/storefront/graphql';

import type { FilterType } from './yarnPatterns.types';
import { patternSuggestionsForYarnQuery } from './yarnPatternsGateway.queries';
import { searchPatternsBySKUsQuery } from './yarnPatternsStorefront.queries';

const PATTERNS_PER_PAGE = 50; // Fetch reasonable amount from Gateway API
const DISPLAY_PER_PAGE = 12; // Display 12 at a time in UI

export const useYarnPatterns = (productGroupId: string) => {
  const filterType = ref<FilterType>('all');
  const retrying = ref(false);
  const loadingMore = ref(false);
  const displayedCount = ref(DISPLAY_PER_PAGE); // Track how many to display

  const queryVariables = {
    yarnProductGroupIdentifier: productGroupId,
    locale: window?.Shopify?.locale || 'en',
    limit: PATTERNS_PER_PAGE
  };

  // Step 1: Get pattern SKUs from Gateway
  const {
    loading: gatewayLoading,
    result: gatewayResult,
    error: gatewayError,
    refetch,
    onResult,
    onError
  } = useQuery(patternSuggestionsForYarnQuery, queryVariables, {
    clientId: 'gateway'
  });

  const patterns = computed(
    () =>
      gatewayResult.value?.crafter?.patternSuggestionsForYarn?.patterns ?? []
  );

  // Client-side pagination: check if there are more patterns to display
  const hasMore = computed(
    () => displayedCount.value < allFilteredPatterns.value.length
  );

  // Helper function to build search query from SKUs
  const buildSearchQuery = (skus: string[]) => {
    return skus.map((sku) => `variants.sku:${sku}`).join(' OR ');
  };

  // Step 2: Get pattern SKUs to build search query
  const searchQuery = computed(() => {
    const skus = patterns.value.map((p) => p.sku).filter(Boolean);
    return skus.length > 0 ? buildSearchQuery(skus) : '';
  });

  // Step 3: Search for products by pattern SKUs (much more direct than collections)
  const {
    result: storefrontResult,
    loading: storefrontLoading,
    error: storefrontError
  } = useQuery(
    searchPatternsBySKUsQuery,
    computed(() => ({
      searchQuery: searchQuery.value,
      country: (window?.Shopify?.country as CountryCode) || CountryCode.Us
    })),
    {
      clientId: 'storefront',
      enabled: computed(() => searchQuery.value.length > 0)
    }
  );

  // Step 4: Create hydrated patterns by matching SKUs with search results
  // ONLY show patterns that have matching Shopify products
  const hydratedPatterns = computed(() => {
    const searchResults = storefrontResult.value?.search?.nodes || [];
    // Filter to only Product results (exclude Article and Page)
    const searchProducts = searchResults.filter(
      (result): result is Extract<typeof result, { __typename?: 'Product' }> =>
        result.__typename === 'Product'
    );

    // Debug logging
    console.log('🔍 Debug Yarn Patterns Hydration (Search-based):');
    console.log('- Gateway patterns:', patterns.value.length);
    console.log('- Search query:', searchQuery.value);
    console.log('- Search products found:', searchProducts.length);
    console.log('- Search result:', storefrontResult.value?.search);

    // If no search results, return empty array
    if (!searchProducts.length) {
      return [];
    }

    // Filter to only patterns with matching Shopify products, then map to hydrated format
    return patterns.value
      .map((gp) => {
        // Find matching product by SKU metafield or variant SKU
        const product = searchProducts.find((p) => {
          // Check metafield SKU first
          const metafieldMatch = p.metafields?.find(
            (m) => m?.key === 'sku' && m?.value === gp.sku
          );
          if (metafieldMatch) return true;

          // Check variant SKUs as fallback
          const variantMatch = p.variants?.nodes?.find((v) => v.sku === gp.sku);
          return !!variantMatch;
        });

        if (!product) {
          console.log(
            `❌ No product found for SKU: ${gp.sku} - EXCLUDING from display`
          );
          return null; // Mark for filtering out
        }

        console.log(`✅ Product found for SKU: ${gp.sku}`, product);

        const getMetafield = (key: string) =>
          product.metafields?.find((m) => m?.key === key)?.value;

        return {
          sku: gp.sku,
          name: gp.name,
          gatewayImage: gp.image || undefined,
          product: {
            id: product.id,
            title: product.title,
            handle: product.handle,
            designer: getMetafield('designer'),
            difficulty: getMetafield('difficulty'),
            patternKind: getMetafield('pattern_kind'),
            image: product.featuredImage
              ? {
                  url: product.featuredImage.url,
                  altText: product.featuredImage.altText || undefined,
                  width: product.featuredImage.width || undefined,
                  height: product.featuredImage.height || undefined,
                  alt: product.featuredImage.altText || undefined
                }
              : undefined,
            price: product.priceRange?.minVariantPrice
              ? {
                  amount: Number(product.priceRange.minVariantPrice.amount),
                  currencyCode: product.priceRange.minVariantPrice.currencyCode
                }
              : product.priceRange?.minVariantPrice,
            compareAtPrice: product.compareAtPriceRange?.minVariantPrice
              ? {
                  amount: Number(
                    product.compareAtPriceRange.minVariantPrice.amount
                  ),
                  currencyCode:
                    product.compareAtPriceRange.minVariantPrice.currencyCode
                }
              : undefined,
            variants: (product.variants?.nodes || []).map((variant) => ({
              ...variant,
              quantityAvailable: variant.quantityAvailable ?? undefined,
              price: variant.price
                ? {
                    amount: Number(variant.price.amount),
                    currencyCode: variant.price.currencyCode
                  }
                : variant.price,
              compareAtPrice: variant.compareAtPrice
                ? {
                    amount: Number(variant.compareAtPrice.amount),
                    currencyCode: variant.compareAtPrice.currencyCode
                  }
                : undefined
            }))
          }
        };
      })
      .filter((pattern) => pattern !== null); // Remove patterns without Shopify products
  });

  // Step 5: Filtering (all available patterns)
  const allFilteredPatterns = computed(() => {
    if (filterType.value === 'all') return hydratedPatterns.value;

    return hydratedPatterns.value.filter((pattern) => {
      const patternKind = pattern.product?.patternKind?.toLowerCase();
      if (filterType.value === 'knitting') {
        return (
          patternKind?.includes('knitting') || patternKind?.includes('knit')
        );
      }
      if (filterType.value === 'crochet') {
        return (
          patternKind?.includes('crochet') || patternKind?.includes('hook')
        );
      }
      return true;
    });
  });

  // Step 6: Client-side pagination (only show displayedCount)
  const filteredPatterns = computed(() => {
    return allFilteredPatterns.value.slice(0, displayedCount.value);
  });

  // Client-side pagination: show more patterns from already fetched data
  const loadMore = async () => {
    if (hasMore.value) {
      loadingMore.value = true;
      // Simulate loading delay for better UX
      await new Promise((resolve) => setTimeout(resolve, 500));
      displayedCount.value += DISPLAY_PER_PAGE;
      loadingMore.value = false;
    }
  };

  const retry = () => {
    retrying.value = true;
    refetch();
  };

  onResult(() => {
    if (retrying.value) {
      retrying.value = false;
    }
  });

  onError(() => {
    if (retrying.value) {
      retrying.value = false;
    }
  });

  const loading = computed(
    () => gatewayLoading.value || storefrontLoading.value
  );
  const error = computed(() => gatewayError.value || storefrontError.value);

  // Reset displayed count when filter changes
  watch(filterType, () => {
    displayedCount.value = DISPLAY_PER_PAGE;
  });

  return {
    patterns: filteredPatterns,
    loading,
    error,
    retry,
    retrying,
    filterType,
    hasMore,
    loadMore,
    loadingMore,
    totalPatterns: computed(() => allFilteredPatterns.value.length),
    displayedCount: computed(() => displayedCount.value)
  };
};
